import React from 'react';
import { Row, Col, Popconfirm, Popover, Tooltip } from 'antd';
import { EditOutlined, DeleteOutlined } from '@ant-design/icons';
import CustomAvatar from '../../CustomComponents/CustomAvatar';
import { ActionButton } from '../../CustomComponents/Table/styledComponents';
import ReInviteIcon from '../../../Assets/Icons/ReInviteIcon';
import { PopoverGlobalStyle } from '../JobList/styledComponents';
import EnableUserIcon from '../../../Assets/Icons/EnableUserIcon';
import {
  StyledFacilityBlocks,
  StyledFacilityContainer,
  FacilityRemainingCount,
  StyledSpan
} from './styledComponents';

const multiSelectFilter = (row, columnId, filterValue) => {
  if (!filterValue || filterValue.length === 0) return true;
  const val = row.getValue(columnId);
  const strVal = val != null ? String(val) : 'N/A';
  return filterValue.includes(strVal);
};

export const createAdminColumns = (
  setSelectedItem,
  setAddDrawerVisible,
  disableUser,
  reInvite,
  notClinicAdmin
) => [
  {
    id: 'avatar',
    header: ' ',
    accessorKey: 'signedUrl',
    size: 35,
    enableSorting: false,
    enableColumnFilter: false,
    cell: ({ row }) => (
      <CustomAvatar
        url={row.original.signedUrl}
        size={35}
      />
    )
  },
  {
    id: 'fullName',
    header: 'Name',
    accessorKey: 'fullName',
    enableSorting: true,
    sortingFn: 'text',
    filterFn: multiSelectFilter,
    cell: ({ getValue }) => {
      const val = getValue();
      return (
        <span title={val}>
          {val?.length > 30 ? val.slice(0, 30) + '...' : val}
        </span>
      );
    }
  },
  {
    id: 'email',
    header: 'Email',
    accessorKey: 'email',
    enableSorting: true,
    filterFn: multiSelectFilter
  },
  {
    id: 'phoneNumber',
    header: 'Phone Number',
    accessorKey: 'phoneNumber',
    enableSorting: false,
    filterFn: (row, columnId, filterValue) => {
      if (!filterValue || filterValue.length === 0) return true;
      const value = row.getValue(columnId);
      if (!value || value.countryCode === null || value.number === null) {
        return filterValue.includes('N/A');
      }
      return filterValue.includes(`+${value.countryCode}-${value.number}`);
    },
    meta: {
      getUniqueValues: rows => {
        const numbers = new Set();
        rows.forEach(row => {
          const value = row.original.phoneNumber;
          if (value && value.countryCode !== null && value.number !== null) {
            numbers.add(`+${value.countryCode}-${value.number}`);
          } else {
            numbers.add('N/A');
          }
        });
        return Array.from(numbers);
      }
    },
    cell: ({ getValue }) => {
      const value = getValue();
      return value && (value.countryCode === null || value.number === null)
        ? ''
        : value && <div>+{value.countryCode + '-' + value.number}</div>;
    }
  },
  {
    id: 'facilities',
    header: 'Facility',
    accessorKey: 'facilities',
    enableSorting: false,
    filterFn: (row, columnId, filterValue) => {
      if (!filterValue || filterValue.length === 0) return true;
      const facilities = row.getValue(columnId) || [];
      return facilities.some(f => filterValue.includes(f.facilityName));
    },
    meta: {
      getUniqueValues: rows => {
        const names = new Set();
        rows.forEach(row => {
          (row.original.facilities || []).forEach(f => {
            if (f.facilityName) names.add(f.facilityName);
          });
        });
        return Array.from(names);
      }
    },
    cell: ({ getValue }) => {
      const FacilityList = getValue() || [];
      return (
        <div>
          {FacilityList.length > 2 && (
            <div
              style={{
                display: 'flex',
                flexDirection: 'row '
              }}
            >
              <StyledFacilityContainer>
                {FacilityList.slice(0, 2).map((item, index) => (
                  <Popover
                    key={index}
                    placement='bottom'
                    content={<h5>{item.facilityName}</h5>}
                  >
                    <StyledFacilityBlocks disabled={item.disabled}>
                      <StyledSpan>{item.facilityName}</StyledSpan>
                    </StyledFacilityBlocks>
                  </Popover>
                ))}
              </StyledFacilityContainer>
              <Popover
                placement='bottom'
                content={
                  <div>
                    {FacilityList.slice(2, FacilityList.length).map((item, index) => (
                      <h5 key={index}>{item.facilityName}</h5>
                    ))}
                  </div>
                }
              >
                <FacilityRemainingCount>
                  +{FacilityList.length - 2}
                </FacilityRemainingCount>
              </Popover>
            </div>
          )}
          {FacilityList.length <= 2 && (
            <div
              style={{
                display: 'flex',
                flexDirection: 'row '
              }}
            >
              <StyledFacilityContainer>
                {FacilityList.map((item, index) => (
                  <Popover
                    key={index}
                    placement='bottom'
                    content={<h5>{item.facilityName}</h5>}
                  >
                    <StyledFacilityBlocks disabled={item.disabled}>
                      <StyledSpan>{item.facilityName}</StyledSpan>
                    </StyledFacilityBlocks>
                  </Popover>
                ))}
              </StyledFacilityContainer>
            </div>
          )}
        </div>
      );
    }
  },
  {
    id: 'actions',
    header: 'Action',
    size: 180,
    enableSorting: false,
    enableColumnFilter: false,
    cell: ({ row }) => {
      const user = row.original;
      return (
        <Row gutter={12}>
          <Col>
            <Tooltip title='Edit user'>
              <ActionButton
                onClick={() => {
                  setSelectedItem(user);
                  setAddDrawerVisible(true);
                }}
                icon={<EditOutlined />}
                disabled={!user.isEnabled}
              />
            </Tooltip>
          </Col>
          <Col>
            <Popconfirm
              title={
                <>
                  {user.isEnabled ? 'Disable' : 'Enable'} {user.fullName} ?
                </>
              }
              onConfirm={() => disableUser(user.id)}
              okText='Yes'
              cancelText='No'
              overlayClassName='popover-delete-confirm'
              disabled={notClinicAdmin}
            >
              <PopoverGlobalStyle />
              <Tooltip title={user.isEnabled ? 'Disable user' : 'Enable user'}>
                <ActionButton
                  disabled={notClinicAdmin}
                  icon={
                    user.isEnabled ? (
                      <DeleteOutlined />
                    ) : (
                      <EnableUserIcon disabled={notClinicAdmin} />
                    )
                  }
                />
              </Tooltip>
            </Popconfirm>
          </Col>
          <Col>
            <Popconfirm
              title='Are you sure you want to re-invite?'
              onConfirm={() => reInvite(user.id)}
              okText='Yes'
              cancelText='No'
              overlayClassName='popover-delete-confirm'
            >
              <Tooltip title='Reinvite user'>
                <ActionButton
                  icon={<ReInviteIcon disabled={!user.isEnabled} />}
                  disabled={!user.isEnabled}
                />
              </Tooltip>
            </Popconfirm>
          </Col>
        </Row>
      );
    }
  },
  {
    id: 'status',
    header: 'Status',
    accessorKey: 'status',
    size: 100,
    enableSorting: false,
    enableColumnFilter: false,
    cell: ({ getValue }) => <span>{getValue() || '—'}</span>
  }
];
